import { useState } from "react";
import { useSelector } from "react-redux";

import { ITEM_CALCULATED_FIELDS } from "appConfig";

import { Decimal } from "classes/DecimalClasses";

import { DeleteLine } from "components/EditPage/DeleteLine";
import { QuickAddItem } from "components/QuickAddItem";
import { LineTableCell } from "components/formFields/LineTableCell";
import { LineClass } from "components/formFields/line/LineClass";
import { LineDescription } from "components/formFields/line/LineDescription";
import { LineInventoryItemBin } from "components/formFields/line/LineInventoryItemBin";
import { LineJobWorkcenter } from "components/formFields/line/LineJobWorkcenter";
import { LineLotSelect } from "components/formFields/line/LineLotSelect";
import { LineQuantity } from "components/formFields/line/LineQuantity";
import { LineSerialsSelect } from "components/formFields/line/LineSerialsSelect";
import { LineTaxCode } from "components/formFields/line/LineTaxCode";
import { LineUom } from "components/formFields/line/LineUom";
import { LineVolumeAndVolumeUnit } from "components/formFields/line/LineVolumeAndVolumeUnit";
import { LineWeightAndWeightUnit } from "components/formFields/line/LineWeightAndWeightUnit";

import {
  getItemRecord,
  clearItemDataFromLine,
} from "services/sosInventoryService/domainLogic";
import { getEmptyRecord as getEmptyItemRecord } from "services/sosInventoryService/item/schema";
import { updateReturnToVendorLineWithItem } from "services/sosInventoryService/purchasingTransaction/domainLogic";
import {
  createPurchasingLineFromItem,
  postItemAndCreatePurchasingLineItem,
} from "services/sosInventoryService/purchasingTransaction/domainLogic";
import { afterTouchLine } from "services/sosInventoryService/returnToVendor/afterTouchLine";
import { setPageDirty } from "services/utility/edit";
import { isLineFieldInError } from "services/utility/errors";
import { formatLotsWithQuantityAndExpiration } from "services/utility/formatting";
import { TYPES } from "services/utility/schema";

import { useItemLocationLots } from "hooks/useItemLocationLots";
import { usePlans } from "hooks/usePlans";

import { OBJECT_TYPES, ITEM_QUICKLIST_FORM_TYPES } from "appConstants";
import { LINE_ITEM_FIELDS, EMPTY_LINE_ITEM } from "editConfig";

const OBJECT_TYPE = OBJECT_TYPES.RETURN_TO_VENDOR.fullString;

// created this wrapper to make drag and drop code easier to see
export function LineItem(props) {
  return <LineItemDetails {...props} />;
}

function LineItemDetails(props) {
  const {
    record,
    line,
    lineHandler,
    expandItemGroup,
    items,
    itemsCount,
    addItem,
    taxCodes,
    classes,
    jobWorkcenters,
    relatedRecords,
    lineItemsErrors: errors,
    setErrors,
  } = props;
  const { location, date } = record;

  const [addNewItem, setAddNewItem] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [lineLoading, setLineLoading] = useState(false);

  // SETTINGS
  const showVolumeOnPurchases = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnPurchases
  );
  const showWeightOnPurchases = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnPurchases
  );
  const defaultAssetAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultAssetAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultCOGSAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultCOGSAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultExpenseAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultExpenseAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultIncomeAccount = useSelector(
    (state) => state.userCompanySettings.settings.defaultIncomeAccount,
    (prev, curr) => curr?.id === prev?.id
  );
  const defaultTaxCode = useSelector(
    (state) => state.userCompanySettings.settings.defaultTaxCode,
    (prev, curr) => curr?.id === prev?.id
  );

  const { hasAtLeastPlusPlan } = usePlans();

  const rawLots = useItemLocationLots(
    line.item?.id,
    location?.name,
    line.bin?.id,
    date,
    line.itemDetails.lotTracking
  );
  const lots = rawLots ? formatLotsWithQuantityAndExpiration(rawLots) : null;

  function isInError(field) {
    return isLineFieldInError(field, line.lineNumber, errors);
  }

  function handleValueChange(fieldName, newValue) {
    let newLine;
    if (LINE_ITEM_FIELDS[OBJECT_TYPE][TYPES.reference].includes(fieldName)) {
      newLine = { ...line, [fieldName]: { id: newValue } };
    } else {
      newLine = { ...line, [fieldName]: newValue };
    }
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  async function handleItemChange(newItem, userInput) {
    if (!newItem) {
      const updatedLine = clearItemDataFromLine(line);
      setPageDirty();
      lineHandler({ type: "update", updatedLine });
      return;
    }
    if (newItem?.id === "add") {
      return openAddItem(userInput);
    }
    setLineLoading(true);
    const item = await getItemRecord(
      newItem.id,
      location?.id,
      date,
      ITEM_CALCULATED_FIELDS[OBJECT_TYPE]
    );
    const newLine = await updateReturnToVendorLineWithItem(item, line, record);
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "item"),
    });

    setLineLoading(false);
  }

  function handleUomChange(_, uom) {
    setPageDirty();
    const updatedLine = afterTouchLine({ ...line, uom }, "uom");
    lineHandler({ type: "update", updatedLine });
  }

  function handleQuantityChange(_, newValue) {
    const quantity = new Decimal(newValue || 0);
    const newLine = { ...line, quantity };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, "quantity"),
    });
  }

  function handleArrayOfReferencesChange(fieldName, newValue) {
    const newLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  function handleReferenceChange(fieldName, newValue) {
    const newLine = { ...line, [fieldName]: newValue };
    setPageDirty();
    lineHandler({
      type: "update",
      updatedLine: afterTouchLine(newLine, fieldName),
    });
  }

  async function onQuickAddItem(name) {
    const newItemSettings = {
      defaultCOGSAccount,
      defaultAssetAccount,
      defaultExpenseAccount,
      defaultIncomeAccount,
      defaultTaxCode,
    };
    const item = { ...getEmptyItemRecord(newItemSettings), name };
    const updatedLine = await postItemAndCreatePurchasingLineItem(
      item,
      EMPTY_LINE_ITEM[OBJECT_TYPE],
      line.lineNumber,
      addItem,
      setErrors
    );
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    setAddNewItem(false);
  }

  function onAddItem(item) {
    setAddNewItem(false);
    const updatedLine = createPurchasingLineFromItem(
      item,
      EMPTY_LINE_ITEM[OBJECT_TYPE],
      line.lineNumber,
      lineHandler
    );
    setPageDirty();
    lineHandler({ type: "update", updatedLine });
    addItem();
  }

  function handleDelete() {
    setPageDirty();
    lineHandler({ type: "delete", deleteAt: line.lineNumber });
  }

  function openAddItem(userInput) {
    // handle "Add new item" in Item select input field; this does not change the
    // line item, so no need to proceed further into the handler
    setNewItemName(userInput);
    setAddNewItem(true);
  }

  if (!line) {
    return null;
  }

  return (
    <>
      <DeleteLine onClick={handleDelete} />
      <LineTableCell>
        <div
          style={{
            cursor: "pointer",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 0.5rem",
          }}
        >
          {line.lineNumber}
        </div>
      </LineTableCell>

      <LineInventoryItemBin
        line={line}
        objectType={OBJECT_TYPE}
        itemFormType={ITEM_QUICKLIST_FORM_TYPES.PURCHASING}
        lineLoading={lineLoading}
        items={items}
        itemsCount={itemsCount}
        addItem={addItem}
        handleItemChange={handleItemChange}
        handleReferenceChange={handleReferenceChange}
        expandItemGroup={expandItemGroup}
        isInError={isInError}
        bins={line.availableBins}
        allowExpand
        location={relatedRecords.location}
      />

      <LineTableCell sx={{ minWidth: "24rem" }}>
        <LineDescription
          value={line.description}
          onValueChange={handleValueChange}
          error={isInError("description")}
        />

        {hasAtLeastPlusPlan && (
          <>
            {line.itemDetails?.serialTracking && (
              <LineSerialsSelect
                objectType={OBJECT_TYPE}
                value={line.serials}
                itemId={line.item.id}
                bin={line.bin}
                location={relatedRecords.location}
                quantity={line.quantity}
                onValueChange={handleArrayOfReferencesChange}
                error={isInError("serials")}
              />
            )}
            {line.itemDetails?.lotTracking && (
              <LineLotSelect
                value={line.lot}
                lots={lots}
                bin={line.bin}
                objectType={OBJECT_TYPE}
                location={relatedRecords.location}
                onValueChange={handleReferenceChange}
              />
            )}
          </>
        )}
      </LineTableCell>

      <LineQuantity
        objectType={OBJECT_TYPE}
        value={line.quantity}
        onValueChange={handleQuantityChange}
        error={isInError("quantity")}
        disabled={lineLoading}
      />

      <LineUom
        value={line.uom}
        onValueChange={handleUomChange}
        itemUoms={line.itemDetails?.itemUoms}
        error={isInError("uom")}
      />

      {showWeightOnPurchases && (
        <LineWeightAndWeightUnit
          weight={line.weight}
          weightunit={line.weightunit}
        />
      )}

      {showVolumeOnPurchases && (
        <LineVolumeAndVolumeUnit
          volume={line.volume}
          volumeunit={line.volumeunit}
        />
      )}

      <LineClass
        value={line.class}
        classes={classes}
        onValueChange={handleReferenceChange}
      />

      <LineTaxCode
        value={line.taxCode}
        taxCodes={taxCodes}
        onValueChange={handleValueChange}
        error={isInError("taxCode")}
      />

      <LineJobWorkcenter
        job={line.job}
        workcenter={line.workcenter}
        value={line.jobWorkcenter}
        jobWorkcenters={jobWorkcenters}
        onValueChange={handleReferenceChange}
      />

      {addNewItem && (
        <QuickAddItem
          open={addNewItem}
          initialNameValue={newItemName}
          onClose={() => setAddNewItem(false)}
          onAdd={onAddItem}
          onQuickAdd={onQuickAddItem}
        />
      )}
    </>
  );
}
